import { TextSegment } from './TextSegment.d';

const MAXIMUM_LCS_MATCH_LENGTH = 3000;
const MAXIMUM_LCS_MATCH_WORDS = 3000;
const QUICK_SCAN_FRAGMENT_WORDS = 10;
const THRESHOLD_FUDGE_FACTOR = 0.8;

interface MatchResult {
  item: TextSegment;
  score: number;
}

function rateTextSegments(
  textSegments: TextSegment[],
  match: string,
  threshold: number,
  isParagraphLevel = false
): MatchResult[] {
  const firstTenWordsOfMatch = match.split(' ').slice(0, QUICK_SCAN_FRAGMENT_WORDS).join(' ');
  const possibleMatches: TextSegment[] = [];
  const results: MatchResult[] = [];

  // First stage: find possible matches based on the first QUICK_SCAN_FRAGMENT_WORDS words
  textSegments.forEach((textSegment) => {
    const firstTenWords = textSegment.text.split(' ').slice(0, QUICK_SCAN_FRAGMENT_WORDS).join(' ');
    const score = longestCommonSequenceScore(firstTenWords, firstTenWordsOfMatch);
    if (score > threshold * THRESHOLD_FUDGE_FACTOR) { possibleMatches.push(textSegment); }
  });

  // Second stage: iterate over possible matches to do a full text comparison
  possibleMatches.forEach((matchResult) => {
    const textA = matchResult.text;
    const textB = match;
    let score = 0;

    if (isParagraphLevel) {
      const wordCountA = textA.trim().split(/\s+/).length;
      const wordCountB = textB.trim().split(/\s+/).length;
      const maxWords = Math.max(wordCountA, wordCountB);

      if (maxWords <= MAXIMUM_LCS_MATCH_WORDS) {
        const textAArray = textA.trim().split(/\s+/);
        const textBArray = textB.trim().split(/\s+/);
        score = longestCommonSequenceScore(textAArray, textBArray);
      }
    } else {
      score = longestCommonSequenceScore(textA, textB);
    }

    if (score > threshold) { results.push({ item: matchResult, score }); }
  });

  return results.sort((a, b) => b.score - a.score);
}

const longestCommonSequence =
  (a: string | string[], b: string | string[], x?: (p: string, q: string) => boolean): string[] => {
    const comparator = typeof x !== 'function' ? ((p: string, q: string): boolean => p === q) : x;
    const m = a.length;
    const n = b.length;
    const C = [];
    let i;
    let j;

    for (i = 0; i <= m; i += 1) C.push([0]);
    for (j = 0; j < n; j += 1) C[0].push(0);
    for (i = 0; i < m; i += 1) {
      for (j = 0; j < n; j += 1) {
        C[i + 1][j + 1] = comparator(a[i], b[j]) ? C[i][j] + 1 : Math.max(C[i + 1][j], C[i][j + 1]);
      }
    }
    return (function d(k: number, l: number): string[] {
      if (k * l === 0) return [];
      if (comparator(a[k - 1], b[l - 1])) return d(k - 1, l - 1).concat(a[k - 1]);
      return (C[k][l - 1] > C[k - 1][l]) ? d(k, l - 1) : d(k - 1, l);
    }(m, n));
  };

const longestCommonSequenceScore =
  (a: string | string[], b: string | string[], x?: (p: string, q: string) => boolean) => {
    const m = a.length;
    const n = b.length;
    const max = Math.max(m, n);
    if (max > MAXIMUM_LCS_MATCH_LENGTH) return 0;
    return m === 0 || n === 0 ? 0 : longestCommonSequence(a, b, x).length / max;
  };

export default rateTextSegments;
